/**
 * PreventivoForm — formulario dedicato per il template "preventivo-pratica".
 *
 * Workflow:
 *   Bozza → Inviato → Accettato → (Trasforma in pratica)
 *
 * Non duplica clienti: il `clientId` selezionato è l'unica source of truth.
 * Numerazione progressiva (00023) auto-assegnata lato backend al primo save.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Save, Download, Trash2, Plus, FileText, Send, CheckCircle2,
  XCircle, ArrowRightCircle, Loader2,
} from "lucide-react";
// NOTA: niente più import `toast` da sonner — sostituito con
// flashStatus inline per evitare race React 19 portal/insertBefore
// (bug fix definitivo 22/02/2026). I messaggi compaiono in una
// banda statica DENTRO il form, mai in portali esterni.
import api from "../api";
import ClientCombobox from "./ClientCombobox";

const STATUS_LABELS = {
  bozza: { label: "Bozza", color: "bg-slate-200 text-slate-700" },
  inviato: { label: "Inviato", color: "bg-blue-100 text-blue-700" },
  accettato: { label: "Accettato", color: "bg-green-100 text-green-700" },
  rifiutato: { label: "Rifiutato", color: "bg-red-100 text-red-700" },
};

function emptyLine() { return { description: "", amount: "" }; }

export default function PreventivoForm({
  editingForm, clients = [], operators = [], cases = [], currentOperator,
  onBack, onSaved, onOpenClientDialog,
}) {
  const isEdit = !!editingForm;
  const editData = editingForm?.formData || {};

  // Inline status messaging (richiesto dal bug fix 22/02/2026):
  // Zero toast/sonner durante save/downloadPdf — evita la race con
  // portal sonner che provocava `insertBefore`/`removeChild` su
  // React 19 Edge/Chromium. Il messaggio compare in una banda
  // statica DENTRO il form, sempre nello stesso DOM tree, mai in
  // un portal esterno → il DOM resta stabile durante async ops.
  const [statusMsg, setStatusMsg] = useState(null); // {type, text}
  const flashStatus = useCallback((type, text, ms = 4000) => {
    setStatusMsg({ type, text });
    if (ms > 0) setTimeout(() => setStatusMsg((s) => (s?.text === text ? null : s)), ms);
  }, []);

  const [clientId, setClientId] = useState(editingForm?.clientId || "");
  const [brandingContext, setBrandingContext] = useState(editingForm?.brandingContext || "agenzia");
  const [quoteNumber, setQuoteNumber] = useState(editingForm?.quoteNumber || editData.quoteNumber || "");
  const [quoteDate, setQuoteDate] = useState(editData.quoteDate || new Date().toISOString().slice(0, 10));
  const [introText, setIntroText] = useState(
    editData.introText || "Come da Vs. gentile richiesta si inoltra preventivo per:"
  );
  const [lineItems, setLineItems] = useState(
    (editData.lineItems && editData.lineItems.length > 0) ? editData.lineItems : [emptyLine()]
  );
  const [closingNotes, setClosingNotes] = useState(
    editData.closingNotes ||
    "Si evidenzia che nel corso della lavorazione della pratica potrebbe essere richiesta ulteriore documentazione ove richiesta dagli Istituti di competenza."
  );
  // Metodo di pagamento — selezione tra 3 opzioni predefinite (26/02/2026).
  // `paymentTerms` mantenuto in payload per retro-compatibilità con
  // preventivi già salvati (sarà valorizzato con la label umana del metodo).
  const PAYMENT_METHODS = [
    { value: "acquisizione",    label: "Pagamento all'acquisizione pratica" },
    { value: "bonifico_iban",   label: "Bonifico Bancario — IBAN IT89W0306939044100000015821" },
    { value: "rimessa_diretta", label: "Rimessa diretta alla presentazione fattura" },
  ];
  const labelForPaymentMethod = (v) => (PAYMENT_METHODS.find((m) => m.value === v) || {}).label || "";
  // Back-compat: se il vecchio paymentTerms matchava una label nota,
  // pre-popoliamo il select; altrimenti default "acquisizione".
  const _backCompatPM = (text) => {
    const t = (text || "").toLowerCase();
    if (!t) return "acquisizione";
    if (t.includes("acquisizione")) return "acquisizione";
    if (t.includes("bonifico") || t.includes("iban")) return "bonifico_iban";
    if (t.includes("rimessa") || t.includes("presentazione fattura")) return "rimessa_diretta";
    return "acquisizione";
  };
  const [paymentMethod, setPaymentMethod] = useState(
    editData.paymentMethod || _backCompatPM(editData.paymentTerms)
  );

  // Modalità totale (label aggiornate 26/02/2026 - revisione utente):
  // `all_inclusive`        → "Totale complessivo comprensivo di IVA, diritti e bolli"
  //                          PDF: una sola riga finale totale.
  // `net_plus_iva_bolli`   → "Compenso Imponibile"
  //                          PDF: righe separate Compenso/IVA/Diritti/Bolli/Totale.
  const TOTAL_MODES = [
    { value: "all_inclusive",      label: "Totale complessivo comprensivo di IVA, diritti e bolli" },
    { value: "net_plus_iva_bolli", label: "Compenso Imponibile" },
  ];
  const [totalMode, setTotalMode] = useState(editData.totalMode || "all_inclusive");
  // Diritti/Bolli — UNIFIED manual numeric input (26/02/2026 rev3).
  // Sostituisce i 2 campi separati `dirittiAmount` + `stampValue`.
  // Default vuoto. Vuoto o 0 → la riga "Diritti/Bolli" NON appare nel PDF.
  // Backward compatibility: se l'editData arriva da preventivi pre-rev3
  // sommiamo dirittiAmount + stampValue come valore iniziale.
  const _legacyDirittiBolliSum = (() => {
    const d = parseFloat(editData.dirittiAmount || 0);
    const s = parseFloat(editData.stampValue || 0);
    const sum = (isNaN(d) ? 0 : d) + (isNaN(s) ? 0 : s);
    return sum > 0 ? sum : null;
  })();
  const [dirittiBolliAmount, setDirittiBolliAmount] = useState(
    editData.dirittiBolliAmount != null && editData.dirittiBolliAmount !== 0
      ? String(editData.dirittiBolliAmount)
      : (_legacyDirittiBolliSum != null ? String(_legacyDirittiBolliSum) : "")
  );

  // Clausola legale di validità (modificabile al volo, salvata nel template).
  const DEFAULT_LEGAL_VALIDITY = "Il presente preventivo ha validità di 30 giorni dalla data di emissione, salvo eventuali variazioni normative, fiscali o contributive intervenute successivamente.";
  const [legalValidityClause, setLegalValidityClause] = useState(
    editData.legalValidityClause || DEFAULT_LEGAL_VALIDITY
  );
  const [quoteStatus, setQuoteStatus] = useState(editingForm?.quoteStatus || "bozza");

  // 15/07/2026 — Note interne del preventivo. Campo libero per motivo
  // di rifiuto, richieste specifiche del cliente, storico decisionale.
  // NON viene incluso nel PDF (il generatore backend legge solo campi
  // noti come `closingNotes`), rimane visibile solo agli operatori
  // sull'app. Coerente con la privacy: le annotazioni interne non
  // devono mai finire nel documento inviato al cliente.
  const [internalNotes, setInternalNotes] = useState(editData.internalNotes || "");

  // Snapshot del payload iniziale: catturato UNA VOLTA al primo render
  // utile per dirty-check del pulsante "Annulla" senza falsi positivi
  // su form nuovo (dove editData è vuoto ma _buildPayload contiene
  // i default del template).
  const initialPayloadRef = useRef(null);
  const [convertedPracticeId, setConvertedPracticeId] = useState(editingForm?.convertedPracticeId || null);

  // 15/07/2026 — Cronologia stati preventivo. Popolata dal backend
  // ad ogni transizione (append a `statusHistory`). Local state per
  // aggiornarla in-place dopo cambi di stato senza ricaricare tutto.
  const [statusHistory, setStatusHistory] = useState(editingForm?.statusHistory || []);

  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [formId, setFormId] = useState(editingForm?.id || null);
  const [nextNumberPreview, setNextNumberPreview] = useState("");

  // 14/07/2026 — Collegamento bidirezionale Preventivo → Pratica.
  // Recupera il numero pratica dal case linkato. Uso la prop `cases`
  // già passata dal parent (evita chiamate `api.getCases()` extra).
  // Fallback fetch on-demand solo se il case non è nella lista (raro:
  // pratica creata da altro operatore in una sessione parallela).
  const linkedCaseNumber = useMemo(() => {
    if (!convertedPracticeId) return "";
    const c = (cases || []).find((x) => x.id === convertedPracticeId);
    return c ? (c.caseNumber || "") : "";
  }, [convertedPracticeId, cases]);

  const openLinkedCase = useCallback(() => {
    if (!convertedPracticeId) return;
    try {
      window.dispatchEvent(new CustomEvent("gi:open-case-from-quote", {
        detail: { caseId: convertedPracticeId, caseNumber: linkedCaseNumber || "" },
      }));
    } catch (_) { /* noop */ }
  }, [convertedPracticeId, linkedCaseNumber]);

  // Preview del prossimo numero progressivo (solo in creazione).
  // 14/07/2026 — La numerazione è ora separata per Gestionale:
  // Agenzia produce "00001", Studio produce "00001/S". La preview
  // si aggiorna live quando l'utente cambia `brandingContext`.
  useEffect(() => {
    if (isEdit) return;
    let cancelled = false;
    const gest = (brandingContext || "agenzia").toLowerCase();
    api.request(`/api/quotes/next-number?gestionale=${encodeURIComponent(gest)}`).then((d) => {
      if (!cancelled) setNextNumberPreview(d?.nextNumber || "");
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isEdit, brandingContext]);

  const selectedClient = clients.find((c) => c.id === clientId) || null;

  const lineItemsTotal = lineItems.reduce((sum, li) => {
    const n = parseFloat(li.amount);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);
  // Calcoli automatici in modalità "Compenso Imponibile" (rev3 26/02/2026):
  //   Compenso Imponibile = somma righe attività
  //   CAP 4%               = Compenso × 0.04
  //   IVA 22%              = (Compenso + CAP) × 0.22
  //   Diritti/Bolli        = input manuale (opzionale, decimali)
  //   Totale finale        = Compenso + CAP + IVA + Diritti/Bolli
  const dirittiBolliNumeric = parseFloat(dirittiBolliAmount);
  const dirittiBolliAddend = isNaN(dirittiBolliNumeric) ? 0 : dirittiBolliNumeric;
  const capRate = 0.04;
  const ivaRate = 0.22;
  const capAuto = lineItemsTotal * capRate;
  const ivaAuto = (lineItemsTotal + capAuto) * ivaRate;
  const total = totalMode === "net_plus_iva_bolli"
    ? lineItemsTotal + capAuto + ivaAuto + dirittiBolliAddend
    : lineItemsTotal;

  const formatEur = (n) =>
    `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  function updateLine(idx, key, value) {
    setLineItems((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
  }

  function addLine() { setLineItems((prev) => [...prev, emptyLine()]); }
  function removeLine(idx) {
    setLineItems((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  }

  function validate() {
    if (!clientId) return "Seleziona un cliente dall'anagrafica.";
    if (!quoteDate) return "Data preventivo obbligatoria.";
    const filled = lineItems.filter((l) => (l.description || "").trim());
    if (filled.length === 0) return "Aggiungi almeno una voce al preventivo.";
    return null;
  }

  async function save() {
    if (saving) return;  // anti doppio click
    const err = validate();
    if (err) { flashStatus("error", err); return; }
    setSaving(true);
    try {
      const payload = _buildPayload();
      const res = formId
        ? await api.updateForm(formId, payload)
        : await api.createForm(payload);

      const newId = res?.id;
      if (newId && newId !== formId) setFormId(newId);
      if (res?.quoteNumber) setQuoteNumber(res.quoteNumber);
      if (res?.quoteStatus) setQuoteStatus(res.quoteStatus);

      const wasEdit = !!formId;
      flashStatus("success", wasEdit
        ? "Preventivo aggiornato"
        : `Preventivo creato (n. ${res?.quoteNumber || ""})`);

      // 21/07/2026 · fix — Dopo il salvataggio ritorna all'elenco
      // Archivio (parent naviga via setActiveView("archive")).
      // Piccolo delay per permettere all'utente di leggere il toast di
      // conferma. Se l'utente è in modalità edit torna sempre alla
      // lista; su nuovo preventivo torna anche lì così vede subito il
      // documento appena creato nell'archivio.
      if (typeof onSaved === "function") {
        setTimeout(() => onSaved(), 900);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Errore salvataggio preventivo:", e);
      flashStatus("error", `Errore salvataggio: ${e?.message || e}`, 8000);
    } finally { setSaving(false); }
  }

  async function downloadPdf() {
    // BUG FIX 22/02/2026 (definitivo): il flusso precedente chiamava
    // `await save()` internamente, e save() faceva 3 setState
    // (formId, quoteNumber, quoteStatus) DURANTE l'await — questi
    // setState scatenavano rerender intermedi che, combinati con il
    // download anchor + portal Toaster, generavano `insertBefore`
    // crash di React 19 + Error Boundary che usciva dal modulo.
    // Adesso linearizziamo tutto: una sola chiamata API, un solo
    // setState a fine flow, niente nesting di async che mutano stato.
    if (downloading) return;  // anti doppio click
    setDownloading(true);
    try {
      let targetId = formId;

      // Auto-save inline se non ancora persistito.
      if (!targetId) {
        const err = validate();
        if (err) { flashStatus("error", err); return; }
        const payload = _buildPayload();
        const res = await api.createForm(payload);
        targetId = res?.id;
        if (!targetId) {
          flashStatus("error", "Salvataggio fallito: riprova.");
          return;
        }
      }

      // Download sincrono lato DOM (append+click+remove unico flush).
      await api.downloadFormPDF(targetId);

      // Aggiorna stato form a fine flow, in batch (un solo render).
      if (!formId && targetId) {
        try {
          const fresh = await api.request(`/api/forms/${targetId}`);
          setFormId(fresh.id);
          if (fresh.quoteNumber) setQuoteNumber(fresh.quoteNumber);
          if (fresh.quoteStatus) setQuoteStatus(fresh.quoteStatus);
        } catch { /* non bloccare */ }
      }

      flashStatus("success", "PDF generato correttamente");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Errore download PDF preventivo:", e);
      flashStatus("error", `Errore download PDF: ${e?.message || e}`, 8000);
    } finally { setDownloading(false); }
  }

  // Costruisce il payload comune (dedup tra save e downloadPdf).
  // Cattura snapshot del payload UNA VOLTA al primo render del form
  // (dopo che gli state e _buildPayload sono inizializzati). Serve come
  // baseline per il dirty-check del pulsante "Annulla". Niente deps:
  // l'aggancio scatta solo al mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      initialPayloadRef.current = JSON.stringify(_buildPayload().formData);
    } catch { /* fail-soft */ }
  }, []);

  function _buildPayload() {
    return {
      formType: "preventivo-pratica",
      formName: "Preventivo Pratica",
      clientId,
      clientName: selectedClient?.fullName || "",
      brandingContext,
      operatorId: currentOperator?.id || "",
      operatorName: currentOperator?.fullName || "",
      quoteNumber: (quoteNumber || "").trim() || undefined,
      formData: {
        clientName: selectedClient?.fullName || "",
        clientPhone: selectedClient?.phone || "",
        clientAddress: "",
        clientTaxCode: selectedClient?.taxCode || "",
        clientVatNumber: selectedClient?.vatNumber || "",
        quoteNumber: (quoteNumber || "").trim() || "",
        quoteDate,
        introText,
        lineItems: lineItems
          .filter((l) => (l.description || "").trim() || (l.amount + "").trim())
          .map((l) => ({
            description: (l.description || "").trim(),
            amount: parseFloat(l.amount) || 0,
          })),
        closingNotes,
        // Back-compat: paymentTerms ora contiene la label umana del metodo
        // (i preventivi pre-26/02/2026 lo leggevano direttamente).
        paymentMethod,
        paymentTerms: labelForPaymentMethod(paymentMethod),
        totalMode,
        // Campo unificato Diritti/Bolli (rev3 26/02/2026). Vuoto → 0 → row collassata in PDF.
        dirittiBolliAmount: totalMode === "net_plus_iva_bolli" ? (parseFloat(dirittiBolliAmount) || 0) : 0,
        legalValidityClause,
        // 15/07/2026 — Note interne (motivo esito, richieste cliente).
        // NON incluse nel PDF (safe by design: il generatore legge
        // esplicitamente solo campi noti, non ha una `dump` generica).
        internalNotes: (internalNotes || "").trim(),
      },
    };
  }

  async function changeStatus(newStatus) {
    if (!formId) { flashStatus("error", "Salva prima di cambiare stato."); return; }
    try {
      await api.request(`/api/forms/${formId}/quote-status`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus }),
      });
      setQuoteStatus(newStatus);
      // 15/07/2026 — Ricarico il form per catturare `statusHistory`
      // aggiornata dal backend (append side-effect).
      try {
        const fresh = await api.getForm(formId);
        if (fresh?.statusHistory) setStatusHistory(fresh.statusHistory);
      } catch (_) { /* silent */ }
      flashStatus("success", `Stato aggiornato: ${STATUS_LABELS[newStatus]?.label || newStatus}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Errore cambio stato preventivo:", e);
      flashStatus("error", `Errore: ${e?.message || e}`, 8000);
    }
  }

  async function convertToPractice() {
    if (!formId) return;
    if (!window.confirm("Convertire il preventivo in una nuova pratica operativa?")) return;
    setConverting(true);
    try {
      const res = await api.request(`/api/forms/${formId}/convert-to-practice`, { method: "POST" });
      setConvertedPracticeId(res.caseId);
      flashStatus("success", `Pratica ${res.caseNumber} creata. La trovi in Registro Pratiche.`, 8000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Errore conversione preventivo:", e);
      flashStatus("error", `Errore conversione: ${e?.message || e}`, 8000);
    } finally { setConverting(false); }
  }

  const statusInfo = STATUS_LABELS[quoteStatus] || STATUS_LABELS.bozza;

  return (
    <div className="space-y-4" data-testid="preventivo-form">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="preventivo-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Indietro
        </Button>
        <div className="flex items-center gap-2">
          <Badge className={statusInfo.color} data-testid="preventivo-status-badge">
            {statusInfo.label}
          </Badge>
          {convertedPracticeId && (
            <button
              type="button"
              onClick={openLinkedCase}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 transition-colors"
              title="Apri la pratica generata"
              data-testid="preventivo-linked-case-badge"
            >
              <CheckCircle2 className="h-3 w-3" />
              {linkedCaseNumber ? `Convertito nella pratica ${linkedCaseNumber}` : "Convertito in pratica"}
            </button>
          )}
        </div>
      </div>

      {/* Inline status banner — sostituisce sonner toast durante
          save/download/conversion. NESSUN portal esterno. */}
      {statusMsg && (
        <div
          className={
            "rounded-md border px-3 py-2 text-sm " +
            (statusMsg.type === "error"
              ? "border-red-300 bg-red-50 text-red-800"
              : "border-green-300 bg-green-50 text-green-800")
          }
          data-testid="preventivo-status-msg"
        >
          {statusMsg.text}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-teal-700" />
            Preventivo Pratica
            {(quoteNumber || nextNumberPreview) && (
              <Badge variant="outline" className="text-sm font-mono">
                N. {quoteNumber || nextNumberPreview}
              </Badge>
            )}
            {/* Etichetta revisione professionale (14/07/2026):
                appare solo in modifica di un preventivo già salvato. */}
            {isEdit && Number(editingForm?.revision || 0) > 0 && (
              <Badge variant="outline" className="text-xs bg-amber-50 border-amber-300 text-amber-800" data-testid="quote-revision-badge">
                Rev. {String(editingForm.revision).padStart(2, "0")}
                {(editingForm.revisionDate || editingForm.updatedAt) && (
                  <> del {(() => {
                    try {
                      const d = new Date(editingForm.revisionDate || editingForm.updatedAt);
                      return d.toLocaleDateString("it-IT");
                    } catch { return ""; }
                  })()}</>
                )}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dati intestazione — Numero + Data sullo stesso rigo
              come da Registro Pratiche (richiesta utente 22/02/2026).
              Tipologia Gestionale su rigo separato sotto. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="quote-number-input">
                Numero Preventivo {isEdit ? "*" : "(anteprima)"}
              </Label>
              <Input
                id="quote-number-input"
                type="text"
                value={quoteNumber}
                placeholder={nextNumberPreview ? nextNumberPreview : "Auto"}
                onChange={(e) => setQuoteNumber(e.target.value)}
                data-testid="preventivo-number"
              />
            </div>
            <div>
              <Label>Data preventivo</Label>
              <Input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)}
                     data-testid="preventivo-date" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipologia Gestionale</Label>
              <Select value={brandingContext} onValueChange={setBrandingContext}>
                <SelectTrigger data-testid="preventivo-branding"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="studio">Studio</SelectItem>
                  <SelectItem value="agenzia">Agenzia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div />
          </div>

          {/* Cliente — autocomplete inline (stesso pattern delle altre
              pagine: Mandato, Diario, Pratica). Ricerca live
              dall'anagrafica clienti con creazione cliente inline se
              il nome digitato non è ancora in archivio. */}
          <div>
            <Label>Cliente *</Label>
            <ClientCombobox
              value={clientId}
              onChange={setClientId}
              clients={clients}
              onCreateNew={onOpenClientDialog ? (typedName) => onOpenClientDialog(typedName) : undefined}
              placeholder="Digita per cercare cliente in anagrafica…"
              testId="preventivo-client"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              I dati cliente vengono letti SEMPRE dall'anagrafica aggiornata,
              mai duplicati. Se il nome non compare digita per intero e
              clicca "Aggiungi nuovo cliente" nel suggerimento.
            </p>
          </div>

          {/* Testo introduttivo */}
          <div>
            <Label>Testo introduttivo</Label>
            <Textarea rows={2} value={introText} onChange={(e) => setIntroText(e.target.value)}
                      data-testid="preventivo-intro" />
          </div>

          {/* Righe attività */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Righe attività</Label>
              <Button type="button" size="sm" variant="outline" onClick={addLine}
                      data-testid="preventivo-add-line">
                <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi voce
              </Button>
            </div>
            <div className="space-y-2 border border-slate-200 rounded-md p-2 bg-slate-50/50">
              {lineItems.map((li, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_140px_36px] gap-2 items-start"
                     data-testid={`preventivo-line-${idx}`}>
                  <Textarea
                    rows={2}
                    placeholder="Descrizione attività…"
                    value={li.description}
                    onChange={(e) => updateLine(idx, "description", e.target.value)}
                    data-testid={`preventivo-line-desc-${idx}`}
                  />
                  <Input
                    type="number" step="0.01" placeholder="0,00"
                    value={li.amount}
                    onChange={(e) => updateLine(idx, "amount", e.target.value)}
                    data-testid={`preventivo-line-amount-${idx}`}
                  />
                  <Button
                    type="button" variant="ghost" size="icon"
                    onClick={() => removeLine(idx)} disabled={lineItems.length <= 1}
                    title="Rimuovi"
                    data-testid={`preventivo-line-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center justify-end pt-2 border-t border-slate-200 mt-2 gap-3 flex-wrap">
                <span className="text-sm font-medium text-slate-700">
                  {totalMode === "net_plus_iva_bolli"
                    ? "Totale finale"
                    : "Totale complessivo"}
                </span>
                <span className="text-base font-bold text-teal-700" data-testid="preventivo-total">
                  {formatEur(total)}
                </span>
              </div>
              {totalMode === "net_plus_iva_bolli" && (
                <div className="text-xs text-slate-500 mt-1 flex justify-end gap-3 flex-wrap">
                  <span>Compenso: {formatEur(lineItemsTotal)}</span>
                  <span>· CAP 4%: {formatEur(capAuto)}</span>
                  <span>· IVA 22%: {formatEur(ivaAuto)}</span>
                  {dirittiBolliAddend > 0 && <span>· Diritti/Bolli: {formatEur(dirittiBolliAddend)}</span>}
                </div>
              )}
            </div>
          </div>

          {/* Modalità totale + Diritti/Bolli unificato (26/02/2026 rev3).
              CAP 4% e IVA 22% sono auto-calcolati: non servono input. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Modalità totale</Label>
              <Select value={totalMode} onValueChange={setTotalMode}>
                <SelectTrigger data-testid="preventivo-total-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TOTAL_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {totalMode === "net_plus_iva_bolli" && (
              <div>
                <Label>Diritti/Bolli (€)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={dirittiBolliAmount}
                  onChange={(e) => setDirittiBolliAmount(e.target.value)}
                  data-testid="preventivo-diritti-bolli-amount"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Vuoto o 0 → non visibile in PDF. CAP 4% e IVA 22% sono auto-calcolati.
                </p>
              </div>
            )}
          </div>

          {/* Note finali + metodo pagamento */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Note finali</Label>
              <Textarea rows={3} value={closingNotes} onChange={(e) => setClosingNotes(e.target.value)}
                        data-testid="preventivo-closing" />
            </div>
            <div>
              <Label>Metodo di pagamento</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="preventivo-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Verrà stampato nel PDF, sotto la sezione "Pagamento".
              </p>
            </div>
          </div>

          {/* Clausola di validità legale — modificabile (template-level) */}
          <div>
            <Label>Clausola di validità (visibile in PDF e preview)</Label>
            <Textarea rows={2} value={legalValidityClause}
                      onChange={(e) => setLegalValidityClause(e.target.value)}
                      data-testid="preventivo-legal-validity" />
            <p className="text-[11px] text-slate-500 mt-1">
              Inserita automaticamente dopo "Pagamento" e prima dell'area firma.
            </p>
          </div>

          {/* 15/07/2026 — Note interne del preventivo: motivo esito,
              richieste specifiche del cliente, storico decisionale.
              NON incluse nel PDF: il generatore backend legge solo
              campi noti (closingNotes, lineItems, ecc.). Le note
              restano visibili solo agli operatori sull'app. */}
          <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3">
            <Label className="flex flex-wrap items-baseline gap-2 text-amber-900">
              <span className="text-xs font-semibold uppercase tracking-wide">📝 Note interne</span>
              <span className="text-[11px] font-normal text-amber-800">
                motivo esito · richieste cliente · storico decisionale — non stampate nel PDF
              </span>
            </Label>
            <Textarea
              rows={3}
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Es. cliente ha chiesto revisione del compenso · rifiutato perché sopra budget · in attesa conferma dopo colloquio del…"
              className="mt-2 bg-white"
              data-testid="preventivo-internal-notes"
            />
          </div>

          {/* 15/07/2026 — Cronologia stati preventivo: tracciamento
              audit di ogni transizione (Bozza → Inviato → Accettato/
              Rifiutato → Convertito). Aggiornata automaticamente dal
              backend ad ogni cambio di stato. Solo display, non
              modificabile dall'utente. */}
          {statusHistory && statusHistory.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="preventivo-status-history">
              <Label className="flex items-baseline gap-2 text-slate-800">
                <span className="text-xs font-semibold uppercase tracking-wide">🕓 Cronologia stati</span>
                <span className="text-[11px] font-normal text-slate-500">
                  storico transizioni con operatore e data
                </span>
              </Label>
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {/* 16/07/2026 — Ordine cronologico inverso: la transizione
                    più recente in alto (idx=0). L'ultima entry riceve un
                    tag "Ultima" colorato per essere identificata a colpo
                    d'occhio dall'operatore. */}
                {statusHistory.slice().reverse().map((h, idx) => {
                  let dt = "";
                  try { dt = h.at ? new Date(h.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""; } catch { dt = ""; }
                  const fromLbl = STATUS_LABELS[h.from]?.label || h.from || "—";
                  const toLbl = STATUS_LABELS[h.to]?.label || h.to || "—";
                  const opLbl = h.operatorName || "Operatore";
                  const isLatest = idx === 0;
                  return (
                    <li key={`${h.at}-${idx}`} className="flex flex-wrap items-center gap-2 py-1 border-b border-slate-200 last:border-b-0">
                      {isLatest && (
                        <span
                          className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          data-testid="preventivo-status-history-latest"
                          title="Transizione più recente"
                        >
                          ● Ultima
                        </span>
                      )}
                      <span className="font-mono text-[11px] text-slate-500">{dt || "—"}</span>
                      <span className="font-medium text-slate-900">{opLbl}</span>
                      <span className="text-slate-400">·</span>
                      <span className="inline-flex items-center gap-1">
                        <span className="text-slate-600">{fromLbl}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-medium text-slate-900">{toLbl}</span>
                      </span>
                      {h.reason && (
                        <span className="text-slate-500 italic">— {h.reason}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Azioni */}
      <div className="flex flex-wrap items-center gap-2 sticky bottom-2 bg-white/80 backdrop-blur border border-slate-200 rounded-md p-3 shadow-sm">
        <Button onClick={save} disabled={saving} data-testid="preventivo-save">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {formId ? "Aggiorna" : "Salva"}
        </Button>
        <Button variant="outline" onClick={downloadPdf} disabled={downloading}
                data-testid="preventivo-pdf">
          {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Stampa PDF
        </Button>
        {/* Annulla (26/02/2026) — chiude il form senza salvare, senza
            autosave, senza alterare lo stato del preventivo. Se ci sono
            modifiche non salvate, chiede conferma. */}
        <Button
          variant="ghost"
          onClick={() => {
            // Confronto col snapshot iniziale del payload (cattura UNA VOLTA
            // al primo mount). Su form nuovo o appena aperto in edit,
            // il snapshot contiene i default → niente falso positivo.
            let hasUnsaved = false;
            try {
              const cur = JSON.stringify(_buildPayload().formData);
              const baseline = initialPayloadRef.current ?? "";
              hasUnsaved = baseline && cur !== baseline;
            } catch { /* fail-soft */ }
            if (hasUnsaved) {
              const ok = window.confirm("Ci sono modifiche non salvate. Vuoi davvero chiudere senza salvare?");
              if (!ok) return;
            }
            if (typeof onBack === "function") onBack();
          }}
          data-testid="preventivo-annulla"
        >
          Annulla
        </Button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {formId && quoteStatus === "bozza" && (
            <Button variant="outline" onClick={() => changeStatus("inviato")} data-testid="preventivo-send">
              <Send className="h-4 w-4 mr-2" /> Segna come inviato
            </Button>
          )}
          {formId && (quoteStatus === "bozza" || quoteStatus === "inviato") && (
            <Button variant="outline" className="border-green-500 text-green-700 hover:bg-green-50"
                    onClick={() => changeStatus("accettato")} data-testid="preventivo-accept">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Accettato
            </Button>
          )}
          {formId && (quoteStatus === "bozza" || quoteStatus === "inviato") && (
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => changeStatus("rifiutato")} data-testid="preventivo-reject">
              <XCircle className="h-4 w-4 mr-2" /> Rifiutato
            </Button>
          )}
          {formId && quoteStatus === "accettato" && !convertedPracticeId && (
            <Button onClick={convertToPractice} disabled={converting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="preventivo-convert">
              {converting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRightCircle className="h-4 w-4 mr-2" />}
              Trasforma in pratica
            </Button>
          )}
          {convertedPracticeId && (
            <button
              type="button"
              onClick={openLinkedCase}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 transition-colors"
              title="Apri la pratica generata"
              data-testid="preventivo-linked-case-badge-bottom"
            >
              <CheckCircle2 className="h-3 w-3" />
              {linkedCaseNumber ? `Pratica ${linkedCaseNumber}` : "Pratica creata"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

